import { mailApiPost } from "@/lib/api/client";
import type { MailAccountSummary } from "@/lib/types/mail";

const GMAIL_OAUTH_MESSAGE = "aurelia-mail-gmail-oauth";
const POPUP_FEATURES = "width=520,height=720,menubar=no,toolbar=no,status=no";

type GmailOAuthMessage = {
  type: typeof GMAIL_OAUTH_MESSAGE;
  success: boolean;
  account?: MailAccountSummary;
  message?: string;
};

export function startGoogleMailOAuth() {
  return mailApiPost<{ success: boolean; authUrl: string }>(
    "accounts/google-oauth-start.php",
    {}
  );
}

function showPopupLoading(popup: Window) {
  try {
    popup.document.title = "Connexion Google…";
    popup.document.body.innerHTML =
      '<p style="font-family:system-ui,sans-serif;padding:24px;color:#444">Connexion Google…</p>';
  } catch {
    /* cross-origin once navigated */
  }
}

function waitForGmailOAuthPopup(popup: Window): Promise<MailAccountSummary> {
  return new Promise((resolve, reject) => {
    const expectedOrigin = window.location.origin;
    let settled = false;

    const cleanup = () => {
      window.clearInterval(closedPoll);
      window.removeEventListener("message", onMessage);
    };

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      const data = event.data as GmailOAuthMessage | null;
      if (!data || data.type !== GMAIL_OAUTH_MESSAGE) return;

      try {
        popup.close();
      } catch {
        /* ignore */
      }

      if (data.success && data.account) {
        finish(() => resolve(data.account!));
      } else {
        finish(() => reject(new Error(data.message || "Connexion Gmail annulée.")));
      }
    };

    window.addEventListener("message", onMessage);

    const closedPoll = window.setInterval(() => {
      if (popup.closed) {
        finish(() => reject(new Error("Connexion Gmail annulée.")));
      }
    }, 400);
  });
}

/** Ouvre la popup immédiatement (geste utilisateur), puis charge l’URL OAuth. */
export async function connectGmailWithGoogle(): Promise<MailAccountSummary> {
  const popup = window.open("about:blank", "aurelia-mail-gmail-oauth", POPUP_FEATURES);

  if (!popup) {
    throw new Error("Popup bloquée — autorisez les fenêtres popup pour ce site.");
  }

  showPopupLoading(popup);

  try {
    const start = await startGoogleMailOAuth();
    if (!start.ok) {
      popup.close();
      throw new Error(start.message);
    }

    popup.location.href = start.data.authUrl;
    return await waitForGmailOAuthPopup(popup);
  } catch (err) {
    try {
      popup.close();
    } catch {
      /* ignore */
    }
    throw err;
  }
}
